import { IonIcon } from '@ionic/react'
import BarChartIcon from '@mui/icons-material/BarChart'
import EvStationIcon from '@mui/icons-material/EvStation'
import { chevronForward } from 'ionicons/icons'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import Button from '../../../components/actions/Button'
import LinkCard from '../../../components/LinkCard'
import { ConsumptionElementWithStats } from '../../../contexts/hooks/useConsumptionElementData'
import useUser from '../../../contexts/hooks/useUser'
import ConsumptionElementDetailInfo from '../../../modals/ConsumptionElementDetailInfo'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import Charging from './charging/Charging'
import CarActionSheet from './components/CarActionSheet'
import CarChargeDoughnut from './components/CarChargeDoughnut'
import ChargingControls from './components/ChargingControls'
import { ElectricCarActionButtons } from './components/ElectricCarActionButtons'
import InterventionList from './components/Interventions'
import { useElectronicCar } from './hooks/useEletronicCar'
import SmartCharging from './smartCharging/smartCharging'
import SolarChargingButton from './solarCharging/SolarChargingButton'

const ElectricCarDetail = () => {
	const { vehicleId } = useParams<{ vehicleId: string }>()
	const location = useLocation<{ element?: ConsumptionElementWithStats }>()
	const history = useHistory()

	const [menuOpen, setMenuOpen] = useState(false)

	const { t } = useTranslation('pages_electricCar', { keyPrefix: 'electricCarDetail' })

	const { user } = useUser()
	const electronicCarHook = useElectronicCar(vehicleId)

	if (!electronicCarHook) return null

	const {
		vehicle,
		vehicleLoading,
		vehicleAtChargingLocation,
		smartChargingStatus,
		setSmartChargingPolicy,
		vehicleName,
		isError,
		interventionIds,
		smartOverride,
	} = electronicCarHook

	return (
		<PageWrapper
			page='electricCar'
			appBarOptions={{
				ellipsisFunction: () => setMenuOpen(true),
				label: vehicleName,
				navigationType: 'back',
			}}
		>
			{interventionIds && <InterventionList interventionIds={interventionIds} />}

			<div
				className={
					vehicleLoading
						? 'content-container loading cursor-default pointer-events-none'
						: 'content-container'
				}
			>
				<CarChargeDoughnut
					hasTimeEstimate={smartChargingStatus?.consideration?.hasTimeEstimate ?? false}
					batteryLevel={vehicle?.chargeState.batteryLevel ?? 0}
					drivingRange={vehicle?.chargeState.range ?? 0}
				/>
				{vehicle?.chargeState.isCharging && (
					<Charging smartOverride={smartOverride} vehicle={vehicle} isError={isError} /> // if Vehicle is charging, we show everything related to charging (state, time estimates etc)
				)}

				<div className='flex flex-col gap-6 xl:gap-8 w-full xl:w-[80%]'>
					<div className='flex flex-col gap-2'>
						<div className='toggle-container  overflow-hidden border bg-white dark:bg-brand-secondary-200-dark border-brand-primary-100-light dark:border-brand-primary-100-dark px-8 rounded-full w-fit mx-auto flex flex-col gap-2'>
							<Button
								className='hollow m-0 w-fit'
								onClick={() =>
									history.push(`/devices/electric-car/${vehicleId}/charging-locations`)
								}
							>
								{vehicleAtChargingLocation
									? `${t('latauspisteessä')}: ${vehicleAtChargingLocation.name}`
									: t('eiLatauspisteessä')}
								<IonIcon icon={chevronForward} />
							</Button>
						</div>
					</div>

					<div className='flex flex-col gap-2'>
						{!vehicle?.smartChargingPolicy.isEnabled && <ChargingControls vehicle={vehicle} />}
						{user?.vehicleLinked && user.panelLinked && !vehicleLoading && <SolarChargingButton />}
					</div>
				</div>

				<SmartCharging
					smartChargingStatus={smartChargingStatus}
					vehicle={vehicle}
					setSmartChargingPolicy={setSmartChargingPolicy}
				/>

				{location.state?.element && (
					<ConsumptionElementDetailInfo
						consumptionElement={location.state.element}
						vehicleId={vehicle?.id ?? ''}
					/>
				)}

				<div id='consumption-element-detail' className='flex flex-col gap-2 w-full'>
					<LinkCard
						title={t('previouscharges')}
						subText={t('previouschargesdesc')}
						clickHandler={() => {
							history.push(`/devices/electric-car/${vehicleId}/charging-history`)
						}}
					>
						<div className='counter rounded-lg bg-brand-primary-100-light dark:bg-brand-primary-200-dark text-brand-secondary-200-light dark:text-brand-secondary-100-dark'>
							<EvStationIcon className='icon' />
						</div>
					</LinkCard>
					<LinkCard
						title={t('chargestatistics')}
						subText={t('chargestatisticsdesc')}
						clickHandler={() => {
							history.push(`/devices/electric-car/${vehicleId}/charging-statistics`)
						}}
					>
						<div className='counter rounded-lg bg-brand-primary-100-light dark:bg-brand-primary-200-dark text-brand-secondary-200-light dark:text-brand-secondary-100-dark'>
							<BarChartIcon className='icon' />
						</div>
					</LinkCard>
				</div>
			</div>

			<ElectricCarActionButtons
				smartOverride={smartOverride}
				vehicle={vehicle}
				vehicleId={vehicle?.id ?? ''}
			/>

			<CarActionSheet
				menuOpen={menuOpen}
				setMenuOpen={setMenuOpen}
				vehicle={vehicle}
				setSmartChargingPolicy={setSmartChargingPolicy}
			/>
		</PageWrapper>
	)
}

export default ElectricCarDetail
